<script setup>
import {onMounted, reactive, ref, defineProps, watch, defineEmits} from 'vue';
import OxdLabel from '@ohrm/oxd/core/components/Label/Label.vue';
import States from '../constants/States';
import TrainingTypes from '../constants/TrainingTypes';
import DeliveryMethods from '../constants/DeliveryMethods';
import MultiChips from '@ohrm/oxd/core/components/Input/MultiSelect/MultiSelectChips.vue';
import {QuillEditor} from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import {required} from '@ohrm/core/util/validation/rules';
import {APIService} from '@/core/util/services/api.service';
import fileInput from '../../orangehrmPimPlugin/components/FileInput.vue';

// Before
const emit = defineEmits(['onCancel', 'afterSubmit']);

const props = defineProps({
  id: {type: Number, default: null},
  departments: {type: Array, default: () => []},
  isModal: {type: Boolean, default: false},
});

// Fields
const isLoading = ref(false);
const employees = ref([]);
const filterEmployees = ref([]);
const externalEmails = ref([]);
const externalNames = ref([]);
const subUnits = ref([]);

// Models
const scheduleModel = {
  session: '',
  date: null,
  hour: null,
  activity: '',
};

// Http Api
const http = new APIService(window.appGlobal.baseUrl, 'api/v2/pim/employees');

// Rules
const rules = ref({
  name: [required],
  trainingType: [required],

  stateId: [required],
  duration: [required],

  comments: [],
});

// Form
const form = reactive({
  name: '',
  status: States[0],
  description: '',

  trainingType: null,
  duration: null,
  startDate: null,
  dueDate: null,

  trainers: [],
  departments: [],
  subUnits: [],
  participants: [],

  deliveryMethod: null,
  detailDelivery: '',

  programa: '',

  schedules: [],

  assessment: false,
  evaluationMethod: '',
  priority: false,

  files: [],

  comments: '',

  removedSchedules: [],
});

const getDepartmentSubUnits = async departments => {
  const departmentIds = departments.map(department => department.id);
  await http
    .request({
      method: 'GET',
      url: `api/training/departments/sub-units`,
      params: {departmentIds},
    })
    .then(response => {
      subUnits.value = response.data;
      subUnits.value.unshift({
        id: 0,
        label: 'All',
      });
    });
};

onMounted(async () => {
  await http
    .getAll({
      includeEmployees: 'onlyCurrent',
      model: 'departments',
      limit: 0, // PULL ALL EMPLOYEES
      sortField: 'employee.firstName',
    })
    .then(response => {
      employees.value = response.data.data.map(employee => {
        return {
          id: employee.empNumber,
          label: `${employee.firstName} ${employee.middleName} ${employee.lastName}`,
          department: employee.department.id,
          subunit: employee.subUnit.id,
        };
      });

      filterEmployees.value = employees.value;
    });

  if (props.id) {
    await http
      .request({
        method: 'GET',
        url: `api/training/${props.id}`,
      })
      .then(async response => {
        response.data.department.forEach(item => {
          form.departments.push(props.departments.find(dep => dep.id == item));
        });

        await getDepartmentSubUnits(form.departments);

        form.name = response.data.title;
        form.status = States.find(item => item.id == response.data.status);
        form.description = response.data.description;
        form.trainingType = TrainingTypes.find(
          item => item.id == response.data.training_type,
        );

        setTimeout(() => {
          if (response.data.training_type == TrainingTypes[1].id) {
            response.data.trainer.forEach(trainer => {
              if (trainer.email != '')
                externalEmails.value.push({label: trainer.email});
              if (trainer.name != '')
                externalNames.value.push({label: trainer.name});
            });
          } else {
            form.trainers = response.data.trainer;
          }
        }, 50);

        form.duration = response.data.duration;
        form.startDate = response.data.start_date;
        form.dueDate = response.data.due_date;
        form.comments = response.data.comments;

        setTimeout(() => {
          response.data.subunit.forEach(item => {
            form.subUnits.push(subUnits.value.find(sub => sub.id == item));
          });
        }, 200);

        response.data.participants.forEach(participant => {
          form.participants.push(
            employees.value.find(emp => emp.id == participant),
          );
        });

        form.deliveryMethod = DeliveryMethods.find(
          item => item.id == response.data.delivery_method,
        );
        form.detailDelivery = response.data.detail;
        form.programa = response.data.details;

        //TODO -> subunits
        form.schedules = response.data.training_schedule;

        form.assessment = response.data.assesment_required;
        form.evaluationMethod = response.data.evaluationMethod;
        form.priority = response.data.priority;

        response.data.files.forEach(file => {
          form.files.push({
            id: form.files.length + 1,
            label: file.split('/')[file.split('/').length - 1],
            version: '',
            value: file,
            uploaded: true,
          });
        });
      });
  }
});

// Methods
const structuredClone = object => {
  return JSON.parse(JSON.stringify(object));
};

const formatTrainers = async () => {
  const totalRows =
    externalEmails.value.length > externalNames.value.length
      ? externalEmails.value.length
      : externalNames.value.length;

  const trainers = [];

  for (let i = 0; i < totalRows; i++) {
    trainers.push({
      name: externalNames.value[i]?.label ?? '',
      email: externalEmails.value[i]?.label ?? '',
    });
  }

  return trainers;
};

// Start Save
const onSave = async () => {
  isLoading.value = true;

  const files = form.files.map(item => {
    return item.value;
  });

  if (form.trainingType?.id == TrainingTypes[1].id) {
    await formatTrainers().then(response => {
      form.trainers = response;
    });
  }

  const formData = new FormData();
  if (props.id) formData.append('id', props.id.toString());

  formData.append('name', form.name);
  formData.append('statusId', form.status.id);
  formData.append('description', form.description);
  formData.append('trainingTypeId', form.trainingType.id);
  formData.append('duration', form.duration);
  formData.append('startDate', form.startDate);
  formData.append('dueDate', form.dueDate);
  if (form.trainers.length > 0) {
    form.trainers.forEach(trainer => {
      formData.append('trainers[]', JSON.stringify(trainer));
    });
  } else {
    formData.append('trainers[]', '');
  }

  if (form.departments.length > 0) {
    form.departments.forEach(department => {
      formData.append('departments[]', department.id);
    });
  }

  if (form.subUnits.length > 0) {
    form.subUnits.forEach(subunit => {
      if (subunit?.id) formData.append('subunits[]', subunit.id);
    });
  }

  if (form.participants.length > 0) {
    form.participants.forEach(participant => {
      formData.append('participants[]', participant.id);
    });
  } else {
    formData.append('participants[]', '');
  }

  formData.append('deliveryMethodId', form.deliveryMethod?.id);
  formData.append('detailDelivery', form.detailDelivery);
  formData.append('programa', form.programa);

  form.schedules.forEach(schedule => {
    formData.append('schedules[]', JSON.stringify(schedule));
  });

  formData.append('assessment', form.assessment);
  formData.append('evaluationMethod', form.evaluationMethod);
  formData.append('priority', form.priority);

  files.forEach(item => {
    formData.append('files[]', item);
  });

  if (props.id != null) {
    form.removedSchedules.forEach(schedulesId => {
      formData.append('removedSchedules[]', schedulesId);
    });
  }

  formData.append('comments', form.comments);

  let url = `api/training`;

  if (props.id != null) {
    url = `${url}?_method=PUT`;
  }

  http
    .request({
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      method: 'POST',
      url: url,
      data: formData,
    })
    .then(response => {
      if (props.isModal) {
        emit('afterSubmit', {id: response.data.id, name: response.data.name});
      } else {
        window.location.assign(
          `${window.appGlobal.baseUrl}/training/viewTrainingPlan`,
        );
      }
    })
    .finally(() => {
      isLoading.value = false;
    });
};
// End Save

const addSession = () => {
  const clone = structuredClone(scheduleModel);
  form.schedules.push(clone);
};

const removeSession = index => {
  if (props.id != null) {
    form.removedSchedules.push(form.schedules[index]?.id);
  }

  form.schedules.splice(index, 1);
};

const addAttachment = event => {
  const id = crypto.randomUUID();
  const file = event.target.files[0];
  const fileName = file.name;
  form.files.push({
    id: id,
    label: fileName,
    version: '',
    value: file,
  });
};

const removeAttachment = id => {
  const index = form.files.findIndex(f => f.id === id);

  form.files.splice(index, 1);
};

const onCancel = () => {
  if (props.isModal) {
    emit('onCancel');
  } else {
    window.location.assign(
      `${window.appGlobal.baseUrl}/training/viewTrainingPlan`,
    );
  }
};

const addExternalEmail = event => {
  externalEmails.value.push({label: event.target.value});
  event.target.value = '';
};

const removeExternalEmail = label => {
  const index = externalEmails.value.findIndex(item => item.label === label);
  externalEmails.value.splice(index, 1);
};

const addExternalName = event => {
  externalNames.value.push({label: event.target.value});
  event.target.value = '';
};

const removeExternalName = label => {
  const index = externalNames.value.findIndex(item => item.label === label);
  externalNames.value.splice(index, 1);
};

// Watches
watch(
  () => form.departments,
  departments => {
    if (departments.find(department => department.id === 0)) {
      departments = props.departments.filter(department => department.id != 0);
    }

    const trainerIds = form.trainers.map(trainer => trainer.id);
    const departmentIds = departments.map(department => department.id);

    filterEmployees.value = employees.value.filter(employee => {
      return (
        !departmentIds.includes(employee.department) &&
        !trainerIds.includes(employee.id)
      );
    });

    subUnits.value = [];
    getDepartmentSubUnits(departments);

    // caso não haja departments selecionados, remove todas as seleções de subUnits
    if (departments.length == 0) form.subUnits = [];
  },
);

watch(
  () => form.departmentSubunits,
  subUnits => {
    const trainerIds = form.trainers.map(trainer => trainer.id);
    const departmentIds = form.departments.map(department => department.id);
    const subUnitIds = subUnits.map(subUnit => subUnit.id);

    filterEmployees.value = employees.value.filter(employee => {
      return (
        !departmentIds.includes(employee.department) &&
        !subUnitIds.includes(employee.subunit) &&
        !trainerIds.includes(employee.id)
      );
    });
  },
);

watch(
  () => form.trainingType,
  trainingType => {
    if (trainingType.id == TrainingTypes[1].id) {
      form.trainers = [{email: '', name: ''}];
    } else form.trainers = [];
  },
);

watch(
  () => form.trainers,
  trainers => {
    if (form.trainingType.id == TrainingTypes[0].id) {
      const trainerIds = trainers.map(trainer => trainer.id);
      const departmentIds = form.departments.map(department => department.id);
      const subUnitIds = form.subUnits.map(subUnit => subUnit.id);

      if (form.participants.length > 0) {
        form.participants = form.participants.filter(
          participant => !trainerIds.includes(participant.id),
        );
      }

      filterEmployees.value = employees.value.filter(employee => {
        return (
          !departmentIds.includes(employee.department) &&
          !subUnitIds.includes(employee.subunit) &&
          !trainerIds.includes(employee.id)
        );
      });
    }
  },
);

const commentsKey = ref(0);

watch(
  () => form.status,
  status => {
    rules.value.comments =
      status && (status?.id == 4 || status?.id == 5) ? [required] : [];

    commentsKey.value++;
  },
);
</script>
<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        <template v-if="id">
          Edit Training
        </template>
        <template v-else>
          Add Training
        </template>
      </oxd-text>

      <oxd-divider />

      <oxd-form :loading="isLoading" @submitValid="onSave">
        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item v-if="id">
              <oxd-input-field
                :value="`TP-${id}`"
                label="ID"
                :disabled="true"
              />
            </oxd-grid-item>
            <oxd-grid-item :class="{'--span-column-2': !id}">
              <oxd-input-field
                v-model="form.name"
                label="Title"
                placeholder="Title"
                :rules="rules.name"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.status"
                label="Status"
                value="Select"
                type="select"
                :options="States"
                :rules="rules.stateId"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-input-field
            v-model="form.description"
            type="textarea"
            label="Description"
            placeholder="Description"
          />
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.trainingType"
                label="Training Type"
                value="Select"
                type="select"
                :options="TrainingTypes"
                :rules="rules.trainingType"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.duration"
                label="Duration"
                placeholder="Duration"
                :rules="rules.duration"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <date-input v-model="form.startDate" label="Start Date" />
            </oxd-grid-item>
            <oxd-grid-item>
              <date-input v-model="form.dueDate" label="Due Date" />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-if="form.trainingType?.id == TrainingTypes[0].id"
                v-model="form.trainers"
                label="Trainer(s)"
                value="Select"
                type="multiselect"
                :options="employees"
              />
              <template
                v-else-if="form.trainingType?.id == TrainingTypes[1].id"
              >
                <!-- <oxd-input-field
                  v-model="form.trainers"
                  label="Trainer(s)"
                  placeholder="Email"
                  value="Select"
                  type="multiselect"
                  :options="[]"
                  
                /> -->
                <div>
                  <oxd-input-field
                    label="Trainer(s) Email(s)"
                    placeholder="Type and press enter to add an email"
                    :required="false"
                    @keydown.enter.prevent="addExternalEmail($event)"
                  />
                  <MultiChips
                    :selected="externalEmails"
                    @chipRemoved="option => removeExternalEmail(option.label)"
                  />
                </div>
              </template>
            </oxd-grid-item>
            <oxd-grid-item>
              <template v-if="form.trainingType?.id == TrainingTypes[1].id">
                <div>
                  <oxd-input-field
                    label="Trainer(s) Name(s)"
                    placeholder="Type and press enter to add an Name"
                    :required="true"
                    @keydown.enter.prevent="addExternalName($event)"
                  />
                  <MultiChips
                    :selected="externalNames"
                    @chipRemoved="option => removeExternalName(option.label)"
                  />
                </div>
              </template>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.departments"
                label="Department(s)"
                :value="
                  form.departments.length > 0 && form.departments?.[0].id === 0
                    ? 'All'
                    : 'Select'
                "
                type="multiselect"
                :options="
                  form.departments.length > 0 && form.departments?.[0].id === 0
                    ? [departments[0]]
                    : departments
                "
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.subUnits"
                label="Sub Unit(s)"
                :value="
                  form.subUnits.length > 0 && form.subUnits?.[0].id === 0
                    ? 'All'
                    : 'Select'
                "
                type="multiselect"
                :options="
                  form.subUnits.length > 0 && form.subUnits?.[0].id === 0
                    ? [subUnits[0]]
                    : subUnits
                "
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.participants"
                label="Participants"
                placeholder="All"
                value="Select"
                type="multiselect"
                :options="filterEmployees"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.deliveryMethod"
                label="Delivery Method"
                value="Select"
                type="select"
                :options="DeliveryMethods"
              />
            </oxd-grid-item>
            <oxd-grid-item class="--span-column-3">
              <oxd-input-field
                v-model="form.detailDelivery"
                label="Detail"
                placeholder="Detail"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <OxdLabel label="Details" />
          <br />
          <QuillEditor
            v-model:content="form.programa"
            theme="snow"
            content-type="html"
          />
        </oxd-form-row>
        <br />
        <oxd-form-row>
          <OxdLabel label="Training Schedule" style="margin-bottom: 0.4rem;" />
          <table class="w-100" style="table-layout: fixed;">
            <tbody>
              <tr v-for="(schedule, index) in form.schedules" :key="index">
                <td class="w-20">
                  <oxd-input-field
                    v-model="schedule.session"
                    label="Session"
                    :placeholder="`Session ${index + 1}`"
                    required
                  />
                </td>
                <td class="w-30">
                  <div class="flex gap-3">
                    <date-input v-model="schedule.date" label="Date" />
                    <time-input v-model="schedule.hour" label="Hour" />
                  </div>
                </td>
                <td>
                  <!--
                  <oxd-input-field
                    v-model="schedule.activity"
                    label="Activities"
                    placeholder="Activities"
                    required
                  />
                  -->
                  <QuillEditor
                    v-model:content="schedule.activity"
                    theme="snow"
                    content-type="html"
                  />
                </td>
                <td style="width: 5%;">
                  <oxd-icon-button
                    name="trash"
                    class="oxd-button--ghost"
                    @click="removeSession(index)"
                  />
                </td>
              </tr>
              <tr>
                <td colspan="4">
                  <oxd-icon-button
                    name="plus"
                    class="oxd-button--secondary"
                    @click="addSession"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </oxd-form-row>
        <br />
        <oxd-input-field
          v-model="form.assessment"
          type="checkbox"
          option-label="Assessment Required"
          :value="!form.assessment"
        />
        <br />
        <oxd-input-field
          v-model="form.priority"
          type="checkbox"
          option-label="Priority"
          :value="!form.priority"
        />
        <br />

        <!--  -->

        <oxd-input-field
          v-model="form.evaluationMethod"
          label="Evaluation Method"
          placeholder="Evaluation Method"
        />

        <br />

        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <div class="oxd-input-group">
                <file-input
                  :required="false"
                  :has-error="showRequiredFiles"
                  @change="addAttachment"
                />
                <span
                  v-if="showRequiredFiles"
                  class="oxd-text oxd-text--span oxd-input-field-error-message oxd-input-group__message"
                >
                  Required
                </span>
              </div>
            </oxd-grid-item>
            <oxd-grid-item class="flex items-center">
              <MultiChips
                :selected="form.files"
                @chipRemoved="file => removeAttachment(file.id)"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <br />

        <oxd-form-row>
          <oxd-input-field
            :key="commentsKey"
            v-model="form.comments"
            type="textarea"
            label="Comments"
            placeholder="Comments"
            :required="form.status?.id == 4 || form.status?.id == 5"
            :rules="rules.comments"
          />
        </oxd-form-row>

        <!-- <oxd-form-row>
          <OxdLabel label="Files" style="margin-bottom: 0.4rem;" />
          <br />
          <oxd-grid :cols="1" class="orangehrm-full-width-grid">
            <oxd-grid-item v-for="(file, index) in form.files" :key="index">
              <table class="w-100" style="table-layout: fixed;">
                <tbody>
                  <td>
                    <file-input v-model="form.files[index]" />
                  </td>
                  <td style="width: 5%;">
                    <oxd-icon-button
                      @click="removeFile(index)"
                      name="trash"
                      class="oxd-button--ghost"
                    />
                  </td>
                </tbody>
              </table>
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-icon-button
                style="margin-top: 5px;"
                @click="addFile"
                name="plus"
                class="oxd-button--secondary"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row> -->
        <br />
        <oxd-divider />
        <oxd-form-actions>
          <required-text />
          <oxd-button
            display-type="ghost"
            :label="$t('general.cancel')"
            @click="onCancel"
          />
          <submit-button />
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>
<style>
.ql-container {
  font-family: 'Nunito Sans';
  font-size: 12px;
}
</style>
<style scoped>
.w-100 {
  width: 100%;
}

.w-20 {
  width: 20%;
}

.w-30 {
  width: 30%;
}

.flex {
  display: flex;
}

.items-center {
  align-items: center;
}

.flex {
  display: flex;
}

.gap-3 {
  gap: 3px;
}

.custom-checkbox-direction {
  flex-direction: row-reverse !important;
  display: flex !important;
  justify-content: flex-end !important;
}
</style>
